import { OTHERWORLD_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/otherworldBackdrops';
import { darkBiome } from '../biomeArchetypes';
import { ATMOSPHERE_PALETTE as P } from '../palette';

/** Crimson / Bloodmoon Reach — an eerie cursed blood-red realm under a low ominous BLOOD-MOON:
 *  a deep scarlet sky, a large red lunar disc + bloody halo, drifting ember/blood motes, sickly
 *  blood-red light shafts, a swelling blood-pool glow, a churning crimson energy bed, rising
 *  cursed embers, a pulsing floor flicker, jagged dark blood-rock spires, dark blood-red haze, and
 *  a deep crimson grade. The LUNAR-DREAD red gap — distinct from volcanic (orange heat-glow) and
 *  corrupted (violet dark-magic): this is deep scarlet, foreboding, blood-moon haunted. */
export const CRIMSON = darkBiome({
  id: 'crimson',
  sky: { from: 0x1a040a, to: 0x5a0a16 },
  celestial: { atX: 0.52, atY: 0.17, color: 0xc41028, haloColor: 0xff3344, size: 132, haloScale: 2.9, coreAlpha: 0.9, haloAlpha: 0.24 },
  motes: { colors: [0xff2b3a, P.lava, 0xff6677], driftPx: 38 },
  rays: { color: 0xc41028, widthMin: 55, widthMax: 135, tiltDeg: 7, periodMinMs: 7000, periodMaxMs: 13000, alphaMax: 0.34 },
  afterRaysLayers: [
    // Cursed blood-red lightning splitting the bleeding sky — sparse, ominous strikes
    // (2 anchors, long intervals) that back-light the blood-rock spires. Reduce-motion off.
    { kind: 'lightning', color: 0xff3344, bolts: 2, intervalMinMs: 5000, intervalMaxMs: 11000, flashAlpha: 0.4, boltAlpha: 0.75 },
  ],
  glow: {
    blobs: [
      { color: 0xc41028, atX: 0.52, atY: 0.17, size: 460, alpha: 0.1 },
      { color: P.lava, atX: 0.5, atY: 0.96, size: 560, alpha: 0.14 },
    ],
    breathe: { periodMs: 5000, min: 0.7, max: 1.35 },
  },
  lightField: { palette: [0xc41028, P.lava, 0xff4455], alphaMax: 0.18, driftRadius: 72, periodMinMs: 6000, periodMaxMs: 12000 },
  extraAtmosphereLayers: [
    // Swirling blood vortex — scarlet→dark-crimson particles spiralling outward
    // from the scene centre (the cursed energy churning under the blood-moon).
    // Saturated reds + alphaEnd 0.5 so the vortex reads over the dark scarlet sky.
    { kind: 'weather', mode: 'swirl', color: 0xff3340, colorEnd: 0x6e0810,
      size: 12, frequencyMs: 170, lifespanMs: 5200, alphaEnd: 0.5,
      swirlRadiusMin: 50, swirlRadiusMax: 360, swirlTurns: 1.5,
      modulators: [{ target: 'density', shape: 'sine', periodMs: 7200, min: 0.45, max: 1.55, phase: 0.25 }] },
  ],
  weather: { mode: 'rise', color: 0xff3340, colorEnd: 0x4e0810,
    frequencyMs: 125, lifespanMs: 3700, gravityY: -15, windX: 16,
    gust: { periodMs: 6800, min: 0.5, max: 1.6, phase: 0.25 } },
  flicker: { color: P.lava, hzMax: 7 },
  parallax: [
    { svg: OTHERWORLD_BACKDROPS.corrupted.far, shape: 'spires', color: 0x2a060e, speed: 0.05, height: 430, alpha: 0.74, detail: 0.9, sway: { ampPx: 12, periodMs: 25000 } },
    { svg: OTHERWORLD_BACKDROPS.corrupted.mid, shape: 'spires', color: 0x1b040a, speed: 0.11, height: 300, alpha: 0.86, detail: 1.2 },
    { svg: OTHERWORLD_BACKDROPS.corrupted.near, shape: 'spires', color: 0x100207, speed: 0.20, height: 185, alpha: 0.95, detail: 1 },
  ],
  fog: { color: 0xa81226, bands: 5, alphaMin: 0.04, alphaMax: 0.12, driftPx: 50, periodMinMs: 9000, periodMaxMs: 16000 },
  closer: {
    vignette: { color: 0x12030a, edgeAlpha: 0.4, washColor: 0xc41028, washAlpha: 0.06 },
    grade: { top: 0xc4203a, bottom: 0x240509, strength: 0.13, blend: 'normal' },
  },
});
